import React, { useState } from 'react';
import { Mail, Linkedin, Clipboard, MessageSquare, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, CANDIDATE_STAGES } from './constants';

export const CandidateActions = ({ candidate, onNoteAdded, onSubmissionToggled, onStageChanged }) => {
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const ck = candidate.candidate_key;

  const copyEmail = () => {
    if (candidate.email) {
      navigator.clipboard.writeText(candidate.email);
      toast.success('Email copied');
    } else { toast.info('No email available'); }
  };

  const copyLinkedIn = () => {
    if (candidate.linkedin_url) {
      window.open(candidate.linkedin_url, '_blank');
    } else { toast.info('No LinkedIn URL'); }
  };

  const copySummary = () => {
    const parts = [candidate.name, candidate.title, candidate.current_company, candidate.location].filter(Boolean);
    navigator.clipboard.writeText(parts.join(' | '));
    toast.success('Candidate summary copied');
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(ck)}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: noteText.trim() }),
      });
      if (res.ok) {
        toast.success('Note saved');
        setNoteText('');
        setShowNote(false);
        if (onNoteAdded) onNoteAdded();
      }
    } catch { toast.error('Failed to save note'); }
    setSaving(false);
  };

  const toggleSubmission = async () => {
    try {
      const token = localStorage.getItem('backoffice_token');
      const newVal = !candidate.in_submission;
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(ck)}/submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ add: newVal }),
      });
      if (res.ok) {
        toast.success(newVal ? 'Added to submission list' : 'Removed from submission list');
        if (onSubmissionToggled) onSubmissionToggled(ck, newVal);
      }
    } catch { toast.error('Failed to update submission'); }
  };

  const updateStage = async (newStage) => {
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/candidates/${encodeURIComponent(ck)}/stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stage: newStage }),
      });
      if (res.ok) {
        toast.success(`Status → ${newStage}`);
        if (onStageChanged) onStageChanged(ck, newStage);
      }
    } catch { toast.error('Failed to update status'); }
  };

  const currentStage = CANDIDATE_STAGES.find(s => s.value === (candidate.stage || 'sourced')) || CANDIDATE_STAGES[0];

  return (
    <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-0.5">
        <select value={candidate.stage || 'sourced'}
          onChange={e => updateStage(e.target.value)}
          className={`px-1.5 py-1 text-[9px] font-bold rounded-md border-0 cursor-pointer appearance-none ${currentStage.cls}`}
          style={{ backgroundImage: 'none', paddingRight: '6px' }}
          data-testid={`action-stage-${ck}`}>
          {CANDIDATE_STAGES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <button onClick={copyEmail} title="Copy email"
          className={`p-1.5 rounded-md transition-all ${candidate.has_email || candidate.email ? 'hover:bg-green-100 text-green-600' : 'text-gray-300 cursor-not-allowed'}`}
          data-testid={`action-email-${ck}`}>
          <Mail className="h-3 w-3" />
        </button>
        <button onClick={copyLinkedIn} title="Open LinkedIn"
          className={`p-1.5 rounded-md transition-all ${candidate.has_linkedin || candidate.linkedin_url ? 'hover:bg-blue-100 text-blue-600' : 'text-gray-300 cursor-not-allowed'}`}
          data-testid={`action-linkedin-${ck}`}>
          <Linkedin className="h-3 w-3" />
        </button>
        <button onClick={copySummary} title="Copy candidate summary"
          className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-all"
          data-testid={`action-copy-${ck}`}>
          <Clipboard className="h-3 w-3" />
        </button>
        <button onClick={() => setShowNote(s => !s)} title="Add note"
          className={`p-1.5 rounded-md transition-all ${showNote ? 'bg-[#0A1F44] text-white' : 'hover:bg-amber-100 text-amber-600'}`}
          data-testid={`action-note-${ck}`}>
          <MessageSquare className="h-3 w-3" />
        </button>
        <button onClick={toggleSubmission} title={candidate.in_submission ? 'Remove from submission list' : 'Add to submission list'}
          className={`p-1.5 rounded-md transition-all ${candidate.in_submission ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300' : 'hover:bg-emerald-50 text-gray-400'}`}
          data-testid={`action-submit-${ck}`}>
          <ListChecks className="h-3 w-3" />
        </button>
      </div>
      {(candidate.notes || []).length > 0 && !showNote && (
        <div className="flex flex-wrap gap-1 mt-0.5">
          {candidate.notes.slice(-2).map((n, ni) => (
            <span key={ni} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[8px] rounded max-w-[150px] truncate" title={n.text}>{n.text}</span>
          ))}
        </div>
      )}
      {showNote && (
        <div className="flex items-center gap-1 mt-1">
          <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note..."
            className="flex-1 px-2 py-1 text-[10px] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            onKeyDown={e => { if (e.key === 'Enter') addNote(); }}
            data-testid={`note-input-${ck}`} />
          <button onClick={addNote} disabled={saving || !noteText.trim()}
            className="px-2 py-1 bg-[#0A1F44] text-white text-[9px] font-bold rounded-md disabled:opacity-50"
            data-testid={`note-save-${ck}`}>
            {saving ? '...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};
